import type { CallLogPage } from "./call-logs";
import { api } from "./client";
import type { TicketJob } from "./tickets";

/**
 * Client for bella-admin-api's customers module (`/v1/customers/*`): the
 * customer panel, the full customer page and the top-bar search. Types mirror
 * the Phase 1 contract (customer-panel contract.md) field for field.
 *
 * `customers.view` opens everything except the phone reveal
 * (`customers.phone.view`). The data of other screens still answers to their
 * own keys: tickets, WhatsApp and call-log items are left out of the activity
 * (named in `hidden`) without them, and the calls route is 403.
 *
 * Phone numbers are always masked ("••••• •4821") except in `revealPhone`.
 */

export type CustomerStatus = "prospect" | "active" | "paused" | "inactive";

export interface Named {
  id: number;
  name: string;
}

export interface CustomerNote {
  id: number;
  text: string;
  /** Null when the author is gone. */
  author: Named | null;
  ticketId: number | null;
  /** "PT591755" — the number to show; null when the note is not on a ticket. */
  ticketNumber: string | null;
  createdAt: number;
  updatedAt: number;
  isActive: boolean;
  /** Oldest first. */
  edits: { id: number; name: string; editedAt: number }[];
  /** The author, or an Owner / Admin. */
  canEdit: boolean;
}

/* ------------------------------------------------------------------ *
 * Search
 * ------------------------------------------------------------------ */

export type SearchMatchField = "phone" | "whatsapp" | "alternate" | "id" | "car" | "name";

export interface CustomerSearchHit {
  id: number;
  name: string;
  phone: string;
  status: CustomerStatus;
  isDeleted: boolean;
  societyName: string | null;
  match: { field: SearchMatchField; value: string };
  openTickets: number;
}

export interface TicketSearchHit {
  id: number;
  number: string;
  title: string;
  customer: Named | null;
  state: string;
}

/* ------------------------------------------------------------------ *
 * Profile
 * ------------------------------------------------------------------ */

export type PlanStatus = "active" | "paused" | "payment_pending";

export interface CustomerPlan {
  subscriptionId: number;
  vehicleId: number | null;
  vehicleNumber: string;
  vehicleModel: string | null;
  planName: string | null;
  frequency: string | null;
  timeSlot: string | null;
  champ: Named | null;
  status: PlanStatus;
  /** YYYY-MM-DD (IST). */
  endsOn: string | null;
  daysLeft: number | null;
  /** YYYY-MM-DD. */
  nextJobDate: string | null;
}

export type AttentionItem =
  | {
      kind: "ticket_overdue";
      severity: "high";
      count: number;
      ticketId: number;
      ticketNumber: string;
      title: string;
      ownerName: string | null;
      overdueMinutes: number;
    }
  | {
      kind: "jobs_missed";
      severity: "medium";
      count: number;
      notAtHome: number;
      vehicleNumbers: string[];
      /** YYYY-MM-DD, newest first, at most 3. */
      dates: string[];
    }
  | {
      kind: "whatsapp_blocked";
      severity: "medium";
      numberLabel: string;
      since: number;
      threadId: number | null;
    }
  | {
      kind: "plan_ending";
      severity: "medium";
      subscriptionId: number;
      vehicleNumber: string;
      endsOn: string;
      daysLeft: number;
    };

export type ContactChannel = "whatsapp" | "call" | "ticket";

export interface CustomerProfile {
  id: number;
  name: string;
  status: CustomerStatus;
  isDeleted: boolean;
  isLoginBlocked: boolean;
  phone: string;
  whatsapp: string | null;
  alternate: string | null;
  email: string | null;
  /** Null: an individual customer. */
  society: Named | null;
  flatNo: string | null;
  city: string | null;
  agent: Named | null;
  supervisor: Named | null;
  coins: number;
  rating: { average: number; count: number } | null;
  appVersion: string | null;
  customerSince: number | null;
  notWantedChamps: Named[];
  tags: { private: string[]; public: string[] };
  plans: CustomerPlan[];
  stats: {
    openTickets: number;
    overdueTickets: number;
    jobs30d: { scheduled: number; done: number; missed: number; notAtHome: number };
    /** The latest inbound contact from the customer. */
    lastContact: { at: number; channel: ContactChannel } | null;
  };
  /** At most 5, most serious first. */
  attention: AttentionItem[];
  /** The latest 3 active notes, newest first. */
  notes: CustomerNote[];
}

export interface RevealedPhones {
  phone: string;
  whatsapp: string | null;
  alternate: string | null;
}

/* ------------------------------------------------------------------ *
 * Activity
 * ------------------------------------------------------------------ */

export const ACTIVITY_KINDS = ["whatsapp", "ticket", "call", "job", "note"] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export type WhatsAppItemStatus = "sent" | "delivered" | "read" | "failed";

export type ActivityItem =
  | {
      kind: "whatsapp";
      id: string;
      at: number;
      threadId: number;
      numberLabel: string;
      direction: "in" | "out";
      author: "customer" | "staff" | "ai" | "automation";
      authorName: string | null;
      text: string;
      templateName: string | null;
      status: WhatsAppItemStatus | null;
      failureReason: string | null;
      ticketIds: number[];
    }
  | {
      kind: "ticket";
      id: string;
      at: number;
      ticketId: number;
      ticketNumber: string;
      event: "opened" | "closed" | "reply" | "note";
      authorName: string | null;
      from: "customer" | "team" | "ai" | "automation" | null;
      text: string | null;
      channel: "app" | "whatsapp" | null;
      title: string;
    }
  | {
      kind: "call";
      id: string;
      at: number;
      callId: number;
      direction: "in" | "out";
      status: string;
      durationSec: number;
      agentName: string | null;
      hasRecording: boolean;
    }
  | {
      kind: "job";
      id: string;
      at: number;
      jobId: number;
      /** YYYY-MM-DD. */
      date: string;
      outcome: "missed" | "not_at_home";
      vehicleNumber: string | null;
      champName: string | null;
      /** Null when there is no champ (or an older API). */
      champId: number | null;
    }
  | { kind: "note"; id: string; at: number; note: CustomerNote };

export interface ActivityPage {
  /** Newest first. */
  items: ActivityItem[];
  nextCursor: string | null;
  /** Kinds left out for lack of permission. */
  hidden: ("whatsapp" | "ticket" | "call")[];
}

export interface ActivityParams {
  kinds?: ActivityKind[];
  numberId?: number | null;
  limit?: number;
}

/* ------------------------------------------------------------------ *
 * Query shapes
 * ------------------------------------------------------------------ */

export interface CustomerJobsParams {
  /** YYYY-MM-DD, IST. At most 92 days from `to`. */
  from: string;
  to: string;
  vehicleId?: number | null;
}

export interface CustomerCallsParams {
  from: string;
  to: string;
  page: number;
  pageSize: number;
}

export const NOTE_MAX = 1000;
/** The column is utf8mb3; the server refuses 4-byte characters with this text. */
export const NOTE_EMOJI_MESSAGE = "Emoji can't be saved in a note";
/** Four-byte UTF-8 characters: emoji and every other astral-plane character. */
export const FOUR_BYTE_CHARACTER = /[\u{10000}-\u{10FFFF}]/u;

export const customerKeys = {
  all: ["customers"] as const,
  profiles: () => [...customerKeys.all, "profile"] as const,
  profile: (id: number) => [...customerKeys.profiles(), id] as const,
  activities: (id: number) => [...customerKeys.all, "activity", id] as const,
  activity: (id: number, params: ActivityParams) =>
    [...customerKeys.activities(id), params] as const,
  notes: (id: number) => [...customerKeys.all, "notes", id] as const,
  jobs: (id: number, params: CustomerJobsParams) =>
    [...customerKeys.all, "jobs", id, params] as const,
  calls: (id: number, params: CustomerCallsParams) =>
    [...customerKeys.all, "calls", id, params] as const,
};

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export const getCustomerProfile = (id: number, signal?: AbortSignal) =>
  api.get<CustomerProfile>(`/customers/${id}`, withSignal(signal));

export function getCustomerActivity(
  id: number,
  params: ActivityParams,
  cursor: string | null,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams();
  if (cursor) query.set("cursor", cursor);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.kinds?.length) query.set("kinds", params.kinds.join(","));
  if (params.numberId) query.set("numberId", String(params.numberId));
  const text = query.toString();
  const suffix = text ? `?${text}` : "";
  return api.get<ActivityPage>(`/customers/${id}/activity${suffix}`, withSignal(signal));
}

export function getCustomerJobs(
  id: number,
  params: CustomerJobsParams,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  if (params.vehicleId) query.set("vehicleId", String(params.vehicleId));
  return api.get<TicketJob[]>(
    `/customers/${id}/jobs?${query.toString()}`,
    withSignal(signal),
  );
}

export function getCustomerCalls(
  id: number,
  params: CustomerCallsParams,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({
    from: params.from,
    to: params.to,
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  return api.get<CallLogPage>(
    `/customers/${id}/calls?${query.toString()}`,
    withSignal(signal),
  );
}

export const getCustomerNotes = (
  id: number,
  include: "active" | "all" = "active",
  signal?: AbortSignal,
) =>
  api.get<CustomerNote[]>(
    `/customers/${id}/notes?include=${include}`,
    withSignal(signal),
  );

export const addCustomerNote = (id: number, input: { text: string; ticketId?: number }) =>
  api.post<CustomerNote>(`/customers/${id}/notes`, input);

export const editCustomerNote = (id: number, noteId: number, text: string) =>
  api.patch<CustomerNote>(`/customers/${id}/notes/${noteId}`, { text });

export const removeCustomerNote = (id: number, noteId: number) =>
  api.delete<unknown>(`/customers/${id}/notes/${noteId}`);

/** POST so nothing caches it; the server logs who looked, never the number. */
export const revealCustomerPhone = (id: number) =>
  api.post<RevealedPhones>(`/customers/${id}/phone`);
